import './Certificates.scss';
import Subtitle from 'components/TextComps/Subtitle';
import { useTranslation } from 'react-i18next';
import { ShopCertificate } from 'types/pricing';
import { ReactComponent as Document } from 'images/icons/DocumentThin.svg';
import { ReactComponent as Download } from 'images/icons/Download.svg';
import Paragraph from 'components/TextComps/Paragraph';
import Button from '@trackman/web-shared-components/components/Button/Button';
import { useMemo, useState } from 'react';
import { useCertificateDownload } from 'utilities/useCertificateDownload';
import defaultCertificateBadge from 'images/Certification/DefaultCertificateBadge.svg';
import { useCartStore } from 'stores/Webshop/CartStore';
import { CartItem } from 'types/cart';
import { AddedToCart } from '../../AddedToCart/AddedToCart';
import { LearnMoreDialog } from '../LearnMoreDialog/LearnMoreDialog';
import { SubscriptionPromptDialog } from '../SubscriptionPromptDialog/SubscriptionPromptDialog';
import { getSubscription, renewablePlan } from 'constants/subscription';
import { addCartItemToLocalStorage } from 'utilities/cartLocalStorageHelper';
import { PrerequisitesTooltip } from 'components/CertificateCard/PrerequisitesTooltip/PrerequisitesTooltip';
import { Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useIsUserMember } from 'queryHooks/permissions';
import { useGetShopCertifications } from 'queryHooks/certificates';
import { useGetSubscriptionPrice } from 'queryHooks/subscriptionPrice';

export const Certificates = () => {
  const { t } = useTranslation();
  const { data: certificates, isPending, isRefetching } = useGetShopCertifications();
  const { data: isPremium, isPending: isPremiumPending } = useIsUserMember();
  const { data: subscriptionPrice } = useGetSubscriptionPrice(renewablePlan);

  const itemsInCart = useCartStore((state) => state.items);

  const addItemToCart = useCartStore((state) => state.addItemToCart);
  const isItemInCart = (certificateId: string) => itemsInCart.some((item) => item.id === certificateId);

  const hasDiscount = (!isPremiumPending && isPremium) || itemsInCart.some((item) => item.id === 'premium');

  const [openedLearnMoreModalCertificateId, setOpenedLearnMoreModalCertificateId] = useState<string | null>(null);
  const [openedSubscriptionPromptModalCertificateId, setOpenedSubscriptionPromptModalCertificateId] = useState<string | null>(null);
  const [hasSubscriptionPromptModalBeenOpened, setHasSubscriptionPromptModalBeenOpened] = useState<boolean>(false);

  const handleAddToCart = (item: CartItem) => {
    if (hasDiscount || hasSubscriptionPromptModalBeenOpened) {
      addItemToCart(item);
      addCartItemToLocalStorage(item);
      return;
    } else {
      openSubscriptionPromptModal(item.id);
    }
  };

  const handleAddToCartFromSubscriptionPrompt = (items: CartItem[]) => {
    items.forEach((item) => handleAddToCart(item));
    setOpenedSubscriptionPromptModalCertificateId(null);
  };

  const openSubscriptionPromptModal = (certificateId: string) => {
    setOpenedSubscriptionPromptModalCertificateId(certificateId);
    setHasSubscriptionPromptModalBeenOpened(true);
  };

  const getCartCertificate = (certificateId: string): CartItem => {
    const certificate = certificates?.find((cert) => cert.id === certificateId);

    return {
      id: certificate?.id ?? '',
      title: certificate?.title ?? '',
      price: certificate?.price ?? 0,
    };
  };

  return (
    <div className='shop-certificates'>
      <div className='shop-certificates-header'>
        <Subtitle>
          <>{t('EnhanceYourSkills', { ns: 'webshop' })}</>
        </Subtitle>
        <span>{t('Get50%Off', { ns: 'webshop' })}</span>
      </div>

      <div className='shop-certificates-list'>
        {!isPending && !isRefetching
          ? certificates?.map((certificate, index) => {
              switch (certificate.status) {
                case 'Completed':
                  return <CompletedCertificate key={index} certificate={certificate} />;
                case 'Owned':
                  return <OwnedCertificate key={index} certificate={certificate} />;
                case 'Unlocked':
                  return (
                    <>
                      <UnlockedCertificate
                        key={index}
                        certificate={certificate}
                        hasDiscount={hasDiscount}
                        isItemInCart={isItemInCart(certificate.id)}
                        addItemToCart={(id: string) => handleAddToCart(getCartCertificate(id))}
                        openLearnMoreDialog={setOpenedLearnMoreModalCertificateId}
                      />
                      {openedLearnMoreModalCertificateId === certificate.id ? (
                        <LearnMoreDialog
                          key={`learn-more-${index}`}
                          open={openedLearnMoreModalCertificateId === certificate.id}
                          certification={certificate}
                          isItemInCart={isItemInCart(certificate.id)}
                          isItemLocked={false}
                          onClose={() => setOpenedLearnMoreModalCertificateId(null)}
                          onAddToCart={(id: string) => handleAddToCart(getCartCertificate(id))}
                        />
                      ) : (
                        <></>
                      )}
                      {openedSubscriptionPromptModalCertificateId === certificate.id ? (
                        <SubscriptionPromptDialog
                          key={`subscription-prompt-${index}`}
                          open={openedSubscriptionPromptModalCertificateId === certificate.id}
                          subscriptionPrice={subscriptionPrice || 0}
                          onClose={() => setOpenedSubscriptionPromptModalCertificateId(null)}
                          onAddOnlyCertificate={() =>
                            handleAddToCartFromSubscriptionPrompt([getCartCertificate(openedSubscriptionPromptModalCertificateId)])
                          }
                          handleAddToCart={() =>
                            handleAddToCartFromSubscriptionPrompt([
                              getCartCertificate(openedSubscriptionPromptModalCertificateId),
                              getSubscription(subscriptionPrice || 0),
                            ])
                          }
                        />
                      ) : (
                        <></>
                      )}
                    </>
                  );
                case 'Locked':
                  return (
                    <>
                      <LockedCertificate
                        key={index}
                        certificate={certificate}
                        hasDiscount={hasDiscount}
                        openLearnMoreDialog={setOpenedLearnMoreModalCertificateId}
                      />
                      {openedLearnMoreModalCertificateId === certificate.id ? (
                        <LearnMoreDialog
                          key={`learn-more-${index}`}
                          open={openedLearnMoreModalCertificateId === certificate.id}
                          certification={certificate}
                          isItemInCart={isItemInCart(certificate.id)}
                          isItemLocked={true}
                          onClose={() => setOpenedLearnMoreModalCertificateId(null)}
                        />
                      ) : (
                        <></>
                      )}
                    </>
                  );
              }
            })
          : Array.from(Array(6)).map((el, index) => <CertificateLoaderCard key={index} />)}
      </div>
    </div>
  );
};

const BadgeImage = ({ badgeImageUrl, certificateTitle }: { badgeImageUrl: string; certificateTitle: string }) => {
  return <img src={badgeImageUrl ? badgeImageUrl : defaultCertificateBadge} alt={certificateTitle ?? ''} />;
};

const CompletedCertificate = ({ certificate }: { certificate: ShopCertificate }) => {
  const { t } = useTranslation();
  const { downloadPdf, isDownloading } = useCertificateDownload();

  return (
    <div className='shop-certificate-card shop-certificate-card__completed'>
      <div className='shop-certificate-card-badge'>
        <BadgeImage badgeImageUrl={certificate.badgeImageUrl} certificateTitle={certificate.title} />
      </div>
      <div className='shop-certificate-content'>
        <Subtitle className='certificate-title'>
          <>{certificate.title}</>
        </Subtitle>
        <Paragraph>
          <>{certificate.description}</>
        </Paragraph>
        <div>
          <div className='file-type'>
            <Document />
            <div>PDF</div>
          </div>
          <div>
            <Subtitle>
              <>{t('CertificationComplete', { ns: 'webshop' })}</>
            </Subtitle>
            <Paragraph>
              <>{t('DownloadYourCertificateHere', { ns: 'webshop' })}</>
            </Paragraph>
          </div>
        </div>
      </div>
      <div className='shop-certificate-actions'>
        <Button
          className='download-button'
          startIcon={<Download />}
          onClick={() => downloadPdf(certificate.id, certificate.title)}
          disabled={isDownloading}
        >
          {t('DownloadPdf', { ns: 'webshop' })}
        </Button>
      </div>
    </div>
  );
};

const OwnedCertificate = ({ certificate }: { certificate: ShopCertificate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className='shop-certificate-card shop-certificate-card__owned'>
      <div className='shop-certificate-card-badge'>
        <BadgeImage badgeImageUrl={certificate.badgeImageUrl} certificateTitle={certificate.title} />
      </div>
      <div className='shop-certificate-content'>
        <Subtitle className='certificate-title'>
          <>{certificate.title}</>
        </Subtitle>
        <Paragraph>
          <>{certificate.description}</>
        </Paragraph>
      </div>
      <div className='shop-certificate-actions'>
        <Button
          className='take-exam-button'
          intent='secondary'
          onClick={() => navigate(`/LearningHub/Modules/${certificate.moduleSlug}/Module?entryId=${certificate.id}&type=certificate`)}
        >
          {t('TakeCertificationExam', { ns: 'webshop' })}
        </Button>
      </div>
    </div>
  );
};

const UnlockedCertificate = ({
  certificate,
  hasDiscount,
  isItemInCart,
  addItemToCart,
  openLearnMoreDialog,
}: {
  certificate: ShopCertificate;
  hasDiscount: boolean;
  isItemInCart: boolean;
  addItemToCart: (id: string) => void;
  openLearnMoreDialog: (certificateId: string) => void;
}) => {
  const { t } = useTranslation();
  const certificatePrice = hasDiscount ? certificate.price * 0.5 : certificate.price;
  const getPriceDescription = useMemo(() => {
    const descriptionPrice = hasDiscount ? certificate.price : certificate.price * 0.5;
    return hasDiscount
      ? t('PriceDescriptionPremiumUser', { ns: 'webshop', price: descriptionPrice })
      : t('PriceDescriptionFreeUser', { ns: 'webshop', price: descriptionPrice });
  }, [hasDiscount, certificate.price]);

  return (
    <div className='shop-certificate-card shop-certificate-card__unlocked'>
      <div className='shop-certificate-card-badge'>
        <BadgeImage badgeImageUrl={certificate.badgeImageUrl} certificateTitle={certificate.title} />
      </div>
      <div className='shop-certificate-content'>
        <Subtitle className='certificate-title'>
          <>{certificate.title}</>
        </Subtitle>
        <Paragraph>
          <>{certificate.description}</>
        </Paragraph>
        <div>
          <h2>${certificatePrice}</h2>
          <Paragraph>
            <>{getPriceDescription}</>
          </Paragraph>
        </div>
      </div>
      <div className='shop-certificate-actions'>
        <Button onClick={() => openLearnMoreDialog(certificate.id)}>{t('LearnMore', { ns: 'webshop' })}</Button>
        {!isItemInCart ? (
          <Button intent='primary' onClick={() => addItemToCart(certificate.id)}>
            {t('AddToCart', { ns: 'webshop' })}
          </Button>
        ) : (
          <AddedToCart />
        )}
      </div>
    </div>
  );
};

const LockedCertificate = ({
  certificate,
  hasDiscount,
  openLearnMoreDialog,
}: {
  certificate: ShopCertificate;
  hasDiscount: boolean;
  openLearnMoreDialog: (certificateId: string) => void;
}) => {
  const { t } = useTranslation();
  const certificatePrice = hasDiscount ? certificate.price * 0.5 : certificate.price;
  const getPriceDescription = useMemo(() => {
    const descriptionPrice = hasDiscount ? certificate.price : certificate.price * 0.5;
    return hasDiscount
      ? t('PriceDescriptionPremiumUser', { ns: 'webshop', price: descriptionPrice })
      : t('PriceDescriptionFreeUser', { ns: 'webshop', price: descriptionPrice });
  }, [hasDiscount, certificate.price]);

  return (
    <div className='shop-certificate-card shop-certificate-card__locked'>
      <PrerequisitesTooltip prerequisitesTitles={certificate.prerequisites.map((p) => p.title)} />

      <div className='shop-certificate-card-badge'>
        <BadgeImage badgeImageUrl={certificate.badgeImageUrl} certificateTitle={certificate.title} />
      </div>
      <div className='shop-certificate-content'>
        <div className='locked-certificate-header'>
          <Subtitle className='certificate-title'>
            <>{certificate.title}</>
          </Subtitle>
        </div>

        <Paragraph>
          <>{certificate.description}</>
        </Paragraph>
        <div>
          <h2>${certificatePrice}</h2>
          <Paragraph>
            <>{getPriceDescription}</>
          </Paragraph>
        </div>
      </div>
      <div className='shop-certificate-actions'>
        <Button className='learn-more-button__locked' onClick={() => openLearnMoreDialog(certificate.id)}>
          {t('LearnMore', { ns: 'webshop' })}
        </Button>
      </div>
    </div>
  );
};

const CertificateLoaderCard = () => {
  return (
    <div className='shop-certificate-loader-card'>
      <Skeleton variant='rectangular' height={180} />
      <Skeleton width={100} height={13} />
      <div>
        <Skeleton width={250} height={13} />
        <Skeleton width={200} height={13} />
      </div>
      <Skeleton width={100} height={13} />
    </div>
  );
};
